import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Radio, Play } from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  webinar_event_save_update,
  get_webinar_event_data,
} from "../serviceconnection/ServiceConnection";

// ── Outside component ─────────────────────────────────────────────────────────
const InputField = ({
  label, placeholder, type = "text", value, onChange, required, textarea,
}) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </label>
    {textarea ? (
      <textarea
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent resize-none"
      />
    ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
      />
    )}
  </div>
);

const SelectField = ({ label, options, value, onChange, required }) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </label>
    <select
      value={value ?? ""}
      onChange={onChange}
      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
    >
      <option value="">Select...</option>
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  </div>
);

const TABS_EVENT = ["upcoming", "ondemand"];
const CATEGORY_OPTIONS = [
  "Live Webinar",
  "Interactive Workshop",
  "In-Person Event",
  "Online Course",
  "Panel Discussion",
];
const STATUS_OPTIONS = [
  "Registration Open",
  "Registration Closed",
  "Sold Out",
  "Recording Available",
  "Coming Soon",
];

const EMPTY_FORM = {
  p_id: 0,
  title: "",
  description: "",
  event_type: "upcoming",
  category: "",
  status_label: "Registration Open",
  // ── Date / Time ──────────────────────────────────────
  start_date: "",
  end_date: "",       // blank = single day
  start_time: "",
  end_time: "",
  // ────────────────────────────────────────────────────
  duration: "",
  speakers_count: "",
  location: "",
  register_link: "",
  // On-demand
  recorded_date: "",
  duration_minutes: "",
  views_count: "",
  video_url: "",
  thumbnail: "",
  status: 1,
  sort_order: 0,
};

export default function WebinarEventForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(EMPTY_FORM);
  const [activeEventTab, setActiveEventTab] = useState("upcoming");
  const [loading, setLoading] = useState(isEdit);
  const [saveLoading, setSaveLoading] = useState(false);

  const sf = useCallback((key, val) => setForm((p) => ({ ...p, [key]: val })), []);

  // ── Fetch if edit ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const fd = new FormData();
        fd.append("main_id", id);
        const res = await server_post_data(get_webinar_event_data, fd);
        const d = res.data?.data ?? {};
        setForm({
          p_id: d.primary_id ?? 0,
          title: d.title ?? "",
          description: d.description ?? "",
          event_type: d.event_type ?? "upcoming",
          category: d.category ?? "",
          status_label: d.status_label ?? "",
          start_date: d.start_date ?? "",
          end_date: d.end_date ?? "",
          start_time: d.start_time ?? "",
          end_time: d.end_time ?? "",
          duration: d.duration ?? "",
          speakers_count: d.speakers_count ?? "",
          location: d.location ?? "",
          register_link: d.register_link ?? "",
          recorded_date: d.recorded_date ?? "",
          duration_minutes: d.duration_minutes ?? "",
          views_count: d.views_count ?? "",
          video_url: d.video_url ?? "",
          thumbnail: d.thumbnail ?? "",
          status: d.status ?? 1,
          sort_order: d.sort_order ?? 0,
        });
        setActiveEventTab(d.event_type ?? "upcoming");
      } catch {
        showError("Failed to load event");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!form.title) { showError("Title is required"); return false; }
    if (
      activeEventTab === "upcoming" &&
      form.end_date &&
      form.start_date &&
      form.end_date < form.start_date
    ) {
      showError("End date cannot be before start date");
      return false;
    }
    return true;
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaveLoading(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await server_post_data(webinar_event_save_update, fd);
      if (res.data?.error) { showError(res.data?.message); return; }
      showSuccess(isEdit ? "Event updated!" : "Event created!");
      navigate("/webinars");
    } catch {
      showError("Something went wrong");
    } finally {
      setSaveLoading(false);
    }
  };

  // ── is multi-day? ─────────────────────────────────────────────────────────
  const isMultiDay = Boolean(form.end_date && form.end_date !== form.start_date);

  if (loading)
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        Loading...
      </div>
    );

  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/webinars")}
            className="w-8 h-8 rounded-lg flex items-center justify-center border border-border hover:bg-muted text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">{isEdit ? "Edit Event" : "Add Event"}</h1>
            <p className="page-subtitle">
              {isEdit ? "Update event details" : "Create a new webinar or event"}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saveLoading}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          <Save className="w-4 h-4" />
          {saveLoading ? "Saving..." : isEdit ? "Update" : "Create"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-5">
        {/* Left — info card */}
        <div className="space-y-4">
          <div className="cms-card p-5 flex flex-col items-center gap-3">
            <div
              className="w-20 h-20 rounded-xl flex items-center justify-center"
              style={{
                background:
                  activeEventTab === "upcoming"
                    ? "hsl(var(--accent)/0.12)"
                    : "hsl(var(--info)/0.12)",
                color:
                  activeEventTab === "upcoming"
                    ? "hsl(var(--accent))"
                    : "hsl(var(--info))",
              }}
            >
              {activeEventTab === "upcoming" ? (
                <Radio className="w-8 h-8" />
              ) : (
                <Play className="w-8 h-8" />
              )}
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-foreground">
                {form.title || "New Event"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {activeEventTab === "upcoming"
                  ? "Upcoming Event"
                  : "On-Demand Recording"}
              </p>
              {/* Multi-day badge */}
              {activeEventTab === "upcoming" && isMultiDay && (
                <span
                  className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  style={{
                    background: "hsl(var(--accent)/0.12)",
                    color: "hsl(var(--accent))",
                  }}
                >
                  Multi-day Event
                </span>
              )}
            </div>
          </div>

          {/* Visibility card */}
          <div className="cms-card p-4 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Visibility
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Visible on site</span>
              <button onClick={() => sf("status", form.status === 1 ? 0 : 1)}>
                {form.status === 1 ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="20" viewBox="0 0 32 20" fill="none">
                    <rect width="32" height="20" rx="10" fill="hsl(var(--success))" />
                    <circle cx="22" cy="10" r="8" fill="white" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="20" viewBox="0 0 32 20" fill="none">
                    <rect width="32" height="20" rx="10" fill="hsl(var(--border))" />
                    <circle cx="10" cy="10" r="8" fill="white" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="section-panel p-0">
          {/* Event type tabs */}
          <div className="flex border-b border-border px-1">
            {TABS_EVENT.map((t) => (
              <button
                key={t}
                onClick={() => { setActiveEventTab(t); sf("event_type", t); }}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap -mb-px ${
                  activeEventTab === t
                    ? "border-accent text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t === "upcoming" ? <Radio className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                {t === "upcoming" ? "Upcoming Event" : "On-Demand Recording"}
              </button>
            ))}
          </div>

          <div className="section-panel-body space-y-4">
            {/* Common */}
            <InputField
              label="Title" placeholder="Event title" required
              value={form.title} onChange={(e) => sf("title", e.target.value)}
            />
            <InputField
              label="Description" placeholder="Brief description..." textarea
              value={form.description} onChange={(e) => sf("description", e.target.value)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                label="Category" required options={CATEGORY_OPTIONS}
                value={form.category} onChange={(e) => sf("category", e.target.value)}
              />
              <SelectField
                label="Status Label" options={STATUS_OPTIONS}
                value={form.status_label} onChange={(e) => sf("status_label", e.target.value)}
              />
            </div>

            {/* ── Upcoming specific ─────────────────────────────────────── */}
            {activeEventTab === "upcoming" && (
              <>
                {/* Date range */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-2">
                    Event Date
                    <span className="ml-1 font-normal text-muted-foreground">
                      (leave End Date blank for single-day)
                    </span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InputField
                      label="Start Date" type="date"
                      value={form.start_date}
                      onChange={(e) => {
                        sf("start_date", e.target.value);
                        // clear end_date if it becomes earlier
                        if (form.end_date && e.target.value > form.end_date)
                          sf("end_date", "");
                      }}
                    />
                    <InputField
                      label="End Date"
                      type="date"
                      value={form.end_date}
                      onChange={(e) => sf("end_date", e.target.value)}
                    />
                  </div>
                </div>

                {/* Time range */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Start Time" placeholder="9:00 AM AEST"
                    value={form.start_time}
                    onChange={(e) => sf("start_time", e.target.value)}
                  />
                  <InputField
                    label="End Time" placeholder="5:00 PM AEST"
                    value={form.end_time}
                    onChange={(e) => sf("end_time", e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Duration" placeholder="80 minutes / Full Day"
                    value={form.duration}
                    onChange={(e) => sf("duration", e.target.value)}
                  />
                  <InputField
                    label="Speakers Count" type="number" placeholder="2"
                    value={form.speakers_count}
                    onChange={(e) => sf("speakers_count", e.target.value)}
                  />
                </div>
                <InputField
                  label="Location (In-Person only)"
                  placeholder="Melbourne Convention Centre"
                  value={form.location}
                  onChange={(e) => sf("location", e.target.value)}
                />
                <InputField
                  label="Register Link" placeholder="https://..."
                  value={form.register_link}
                  onChange={(e) => sf("register_link", e.target.value)}
                />
              </>
            )}

            {/* ── On-Demand specific ───────────────────────────────────── */}
            {activeEventTab === "ondemand" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Recorded Date" type="date"
                    value={form.recorded_date}
                    onChange={(e) => sf("recorded_date", e.target.value)}
                  />
                  <InputField
                    label="Duration (minutes)" type="number" placeholder="45"
                    value={form.duration_minutes}
                    onChange={(e) => sf("duration_minutes", e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label="Views Count" type="number" placeholder="342"
                    value={form.views_count}
                    onChange={(e) => sf("views_count", e.target.value)}
                  />
                  <InputField
                    label="Video URL" placeholder="https://youtube.com/..."
                    value={form.video_url}
                    onChange={(e) => sf("video_url", e.target.value)}
                  />
                </div>
                <InputField
                  label="Thumbnail URL" placeholder="https://..."
                  value={form.thumbnail}
                  onChange={(e) => sf("thumbnail", e.target.value)}
                />
              </>
            )}

            {/* Sort order */}
            <InputField
              label="Sort Order" type="number" placeholder="0"
              value={form.sort_order}
              onChange={(e) => sf("sort_order", e.target.value)}
            />

            {/* Bottom save */}
            <div className="flex justify-end pt-2 border-t border-border">
              <button
                onClick={handleSave}
                disabled={saveLoading}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "hsl(var(--accent))" }}
              >
                <Save className="w-4 h-4" />
                {saveLoading ? "Saving..." : isEdit ? "Update Event" : "Create Event"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}